// Gauge Datum Wizard: import a field tide-gauge CSV, QC it, compare it against a nearby CO-OPS
// control station, and compute a preliminary local tidal datum. Walks the same steps NOAA's own
// workflow does (see docs/research/tide-tool.md section 6): describe the gauge -> upload+QC ->
// pick a control station -> choose a comparison method -> compute -> export a report. The
// computation itself (providers/datum-engine.js) is a simplified, from-scratch reimplementation,
// not a port of NOAA's TADC -- every result in this tool is labeled "preliminary".
import { createNoaaCoopsClient } from "../tides/providers/noaa-coops.js";
import { parseGaugeCsv, qcClean, extractHiLo, establishDatums } from "./providers/datum-engine.js";

const STATE_KEY = "hydrotools:gauge-datum-wizard:v1";
const STEPS = [
  { id: "metadata", label: "1. Gauge" },
  { id: "upload", label: "2. Upload & QC" },
  { id: "control", label: "3. Control station" },
  { id: "results", label: "4. Compute & export" },
];

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function asNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function fmt(v, digits = 3) {
  return Number.isFinite(v) ? v.toFixed(digits) : "--";
}

function fmtTime(ms) {
  const d = new Date(ms);
  return Number.isFinite(d.getTime()) ? d.toISOString().replace("T", " ").slice(0, 16) + "Z" : "--";
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      step: STEPS.some((s) => s.id === x.step) ? x.step : "metadata",
      sensorType: typeof x.sensorType === "string" ? x.sensorType : "pressure",
      installIso: typeof x.installIso === "string" ? x.installIso : "",
      removalIso: typeof x.removalIso === "string" ? x.removalIso : "",
      benchmarkNotes: typeof x.benchmarkNotes === "string" ? x.benchmarkNotes : "",
      gaugeLat: asNum(x.gaugeLat, 37.8063),
      gaugeLon: asNum(x.gaugeLon, -122.4659),
      units: x.units === "ft" ? "ft" : "m",
      method: ["auto", "mmsc", "tbyt", "fred"].includes(x.method) ? x.method : "auto",
      controlStationId: typeof x.controlStationId === "string" ? x.controlStationId : "",
    };
  } catch {
    return { step: "metadata", sensorType: "pressure", installIso: "", removalIso: "", benchmarkNotes: "", gaugeLat: 37.8063, gaugeLon: -122.4659, units: "m", method: "auto", controlStationId: "" };
  }
}

function saveState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function downloadText(filename, mime, text) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const gaugeDatumWizardTool = {
  id: "gauge-datum-wizard",
  name: "Gauge Datum Wizard",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const noaa = createNoaaCoopsClient();
  let containerEl = null;
  let state = readState();
  let busy = false;
  let status = "";

  // Heavy, non-persisted working data.
  let fileName = null;
  let qc = null; // { clean, removed, gaps, recordDays, startT, endT }
  let subordinateHilo = [];
  let candidates = [];
  let result = null;

  function persist() { saveState(state); }
  function setStatus(text) { status = String(text || ""); render(); }
  function goStep(id) { state.step = id; persist(); render(); }

  function readForm() {
    if (!containerEl) return;
    const q = (id) => containerEl.querySelector(id);
    state.sensorType = q("#gw-sensor")?.value || state.sensorType;
    state.installIso = q("#gw-install")?.value || state.installIso;
    state.removalIso = q("#gw-removal")?.value || state.removalIso;
    state.benchmarkNotes = q("#gw-benchmarks")?.value ?? state.benchmarkNotes;
    state.gaugeLat = asNum(q("#gw-lat")?.value, state.gaugeLat);
    state.gaugeLon = asNum(q("#gw-lon")?.value, state.gaugeLon);
    state.units = q("#gw-units")?.value === "ft" ? "ft" : "m";
    state.method = q("#gw-method")?.value || state.method;
    state.controlStationId = q("#gw-control")?.value || state.controlStationId;
    persist();
  }

  async function onFile(file) {
    if (!file) return;
    fileName = file.name;
    busy = true;
    setStatus(`Parsing ${file.name}...`);
    try {
      const text = await file.text();
      let rows = parseGaugeCsv(text);
      if (state.units === "ft") rows = rows.map((r) => ({ t: r.t, v: r.v * 0.3048 })); // normalize to meters internally
      qc = qcClean(rows);
      subordinateHilo = extractHiLo(qc.clean);
      result = null;
      setStatus(qc.clean.length
        ? `Loaded ${qc.clean.length.toLocaleString()} readings over ${qc.recordDays.toFixed(1)} days (${qc.removed} point(s) dropped as spikes/duplicates, ${qc.gaps.length} gap(s) over 6h). Extracted ${subordinateHilo.length} high/low events.`
        : "No usable (timestamp, water level) rows found in that file.");
    } catch (err) {
      qc = null;
      setStatus(`Could not parse that file: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  async function findStations() {
    readForm();
    busy = true;
    setStatus("Finding nearby CO-OPS stations...");
    try {
      candidates = await noaa.findNearestStations(state.gaugeLat, state.gaugeLon, 8);
      if (!state.controlStationId && candidates[0]) state.controlStationId = candidates[0].station.id;
      persist();
      setStatus(`${candidates.length} nearby station(s) found.`);
    } catch (err) {
      setStatus(`Station lookup failed: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  async function runCompute() {
    readForm();
    if (!qc?.clean?.length) { setStatus("Upload a gauge CSV first (step 2)."); return; }
    busy = true;
    setStatus("Fetching control station data and computing preliminary datums...");
    try {
      let control = null;
      if (state.method !== "fred" && state.controlStationId) {
        const padMs = 2 * 86_400_000;
        const start = new Date(qc.startT - padMs).toISOString();
        const end = new Date(qc.endT + padMs).toISOString();
        const [series, datumsRes] = await Promise.all([
          noaa.fetchSeries({ stationId: state.controlStationId, datum: "MLLW", units: "metric", start, end }),
          noaa.fetchStationDatums(state.controlStationId).catch(() => ({ datums: [] })),
        ]);
        control = { hilo: series.hilo, observed: series.observed, datums: datumsRes.datums };
      }
      result = establishDatums({
        subordinateHilo,
        subordinateObserved: qc.clean,
        control,
        method: state.method,
        recordDays: qc.recordDays,
      });
      result.controlStationId = state.controlStationId;
      result.controlStationName = candidates.find((c) => c.station.id === state.controlStationId)?.station.name || "";
      result.controlDatums = control?.datums || [];
      setStatus(`Computed with method "${result.method.toUpperCase()}".`);
      state.step = "results";
      persist();
    } catch (err) {
      result = null;
      setStatus(`Computation failed: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  function buildReportHtml() {
    const rows = [
      ["MHHW", result.stats.MHHW], ["MHW", result.stats.MHW], ["MTL", result.stats.MTL],
      ["DTL", result.stats.DTL], ["MLW", result.stats.MLW], ["MLLW", result.stats.MLLW],
    ];
    const datumRows = rows.map(([name, v]) => `<tr><td>${name}</td><td style="text-align:right;">${fmt(v, 3)} m</td></tr>`).join("");
    return `<!doctype html>
<html><head><meta charset="utf-8"><title>Preliminary tidal datum -- ${esc(fileName || "field gauge")}</title>
<style>
  body{font:14px/1.5 system-ui,sans-serif;max-width:720px;margin:32px auto;color:#111;padding:0 16px;}
  h1{font-size:20px;margin-bottom:4px;} h2{font-size:15px;margin-top:24px;border-bottom:1px solid #ccc;padding-bottom:4px;}
  table{border-collapse:collapse;width:100%;margin-top:6px;} td,th{padding:4px 8px;border-bottom:1px solid #eee;}
  .warn{background:#fff6e5;border:1px solid #e7c378;padding:10px 12px;border-radius:6px;margin-top:16px;}
  .muted{color:#666;}
</style></head><body>
  <h1>Preliminary tidal datum report</h1>
  <div class="muted">Generated ${esc(new Date().toISOString())} by HydroTools Gauge Datum Wizard</div>

  <h2>Gauge</h2>
  <table>
    <tr><td>File</td><td>${esc(fileName || "--")}</td></tr>
    <tr><td>Sensor type</td><td>${esc(state.sensorType)}</td></tr>
    <tr><td>Install date</td><td>${esc(state.installIso || "--")}</td></tr>
    <tr><td>Removal date</td><td>${esc(state.removalIso || "--")}</td></tr>
    <tr><td>Location</td><td>${fmt(state.gaugeLat, 5)}, ${fmt(state.gaugeLon, 5)}</td></tr>
    <tr><td>Benchmark / leveling notes</td><td>${esc(state.benchmarkNotes || "--")}</td></tr>
    <tr><td>Record span</td><td>${fmtTime(qc.startT)} to ${fmtTime(qc.endT)} (${qc.recordDays.toFixed(1)} days, ${qc.clean.length.toLocaleString()} readings, ${qc.removed} dropped, ${qc.gaps.length} gap(s) &gt;6h)</td></tr>
  </table>

  <h2>Method</h2>
  <table>
    <tr><td>Comparison method</td><td>${esc(result.method.toUpperCase())}</td></tr>
    <tr><td>Reference frame</td><td>${esc(result.frame)}</td></tr>
    <tr><td>Control station</td><td>${esc(result.controlStationName || "--")} (${esc(result.controlStationId || "none")})</td></tr>
    <tr><td>Computed offset</td><td>${fmt(result.offset, 4)} m${result.offsetInfo?.pairCount ? ` (${result.offsetInfo.pairCount} paired hi/lo events)` : result.offsetInfo?.overlapDays ? ` (${result.offsetInfo.overlapDays.toFixed(1)} day overlap)` : ""}</td></tr>
    <tr><td>Rough error estimate</td><td>&plusmn; ${fmt(result.errorM, 3)} m (illustrative only -- see note below)</td></tr>
  </table>

  <h2>Computed datums</h2>
  <table><thead><tr><th>Datum</th><th style="text-align:right;">Value</th></tr></thead><tbody>${datumRows}</tbody></table>

  <div class="warn">
    <strong>Preliminary, not a certified NOS tidal datum.</strong> This report was produced by a simplified,
    in-house tool, not NOAA's official curve-fit/manual-verification process. The error estimate above is a
    rough, illustrative heuristic scaled by record length -- it is NOT NOAA's calibrated error model. For a
    certified tidal datum, submit this gauge record to NOAA CO-OPS or use their Tidal Analysis Datum
    Calculator (access.co-ops.nos.noaa.gov/datumcalc/).
  </div>
</body></html>`;
  }

  function exportReport() {
    if (!result) return;
    downloadText(`tide-datum-preliminary-${(fileName || "gauge").replace(/\.[^.]+$/, "")}.html`, "text/html;charset=utf-8", buildReportHtml());
  }

  function exportHiLoCsv() {
    if (!subordinateHilo.length) return;
    const rows = [["time_utc", "type", "value_raw_m"], ...subordinateHilo.map((e) => [new Date(e.t).toISOString(), e.type, fmt(e.v, 4)])];
    downloadText("gauge-hilo-events.csv", "text/csv;charset=utf-8", rows.map((r) => r.join(",")).join("\n"));
  }

  function renderStepper() {
    return `<div class="row lm-add-row" style="flex-wrap:wrap;">${STEPS.map((s) => `
      <button type="button" class="btn${state.step === s.id ? " primary" : ""}" data-gw-step="${s.id}">${esc(s.label)}</button>
    `).join("")}</div>`;
  }

  function renderMetadataStep() {
    return `
      <div class="lm-title" style="margin-top:10px;">Sensor</div>
      <div class="row lm-add-row">
        <select id="gw-sensor" style="flex:1;">
          ${["acoustic", "pressure", "bubbler", "float/staff", "gnss-ir", "other"].map((t) => `<option value="${esc(t)}"${state.sensorType === t ? " selected" : ""}>${esc(t)}</option>`).join("")}
        </select>
        <select id="gw-units">
          <option value="m"${state.units === "m" ? " selected" : ""}>meters</option>
          <option value="ft"${state.units === "ft" ? " selected" : ""}>feet</option>
        </select>
      </div>
      <div class="row lm-add-row" style="margin-top:6px;">
        <label class="small" style="min-width:70px;">Install</label>
        <input id="gw-install" type="date" value="${esc(state.installIso)}" style="flex:1;" />
        <label class="small" style="min-width:70px;">Removal</label>
        <input id="gw-removal" type="date" value="${esc(state.removalIso)}" style="flex:1;" />
      </div>
      <div class="lm-title" style="margin-top:10px;">Location</div>
      <div class="row lm-add-row">
        <label class="small" style="min-width:48px;">Lat</label>
        <input id="gw-lat" type="number" step="0.0001" value="${esc(state.gaugeLat)}" style="flex:1;" />
        <label class="small" style="min-width:48px;">Lon</label>
        <input id="gw-lon" type="number" step="0.0001" value="${esc(state.gaugeLon)}" style="flex:1;" />
      </div>
      <div class="lm-title" style="margin-top:10px;">Benchmarks / leveling notes</div>
      <textarea id="gw-benchmarks" rows="3" style="width:100%;" placeholder="e.g. tied to PID AB1234, 2 bracketing level sets, max closure 3mm">${esc(state.benchmarkNotes)}</textarea>
      <div class="small" style="margin-top:8px; color:var(--muted);">
        CO-OPS requires at least two bracketing sets of levels between the sensor and a benchmark network to
        catch sensor/benchmark movement -- note that here even if this tool doesn't check it for you.
      </div>
      <div class="row lm-add-row" style="margin-top:10px;">
        <button id="gw-next-1" class="btn primary">Next: Upload &amp; QC</button>
      </div>
    `;
  }

  function renderUploadStep() {
    const qcSummary = qc ? `
      <div class="small" style="margin-top:8px;">
        ${qc.clean.length.toLocaleString()} readings, ${fmtTime(qc.startT)} to ${fmtTime(qc.endT)}
        (${qc.recordDays.toFixed(1)} days) &middot; ${qc.removed} point(s) dropped as spikes/duplicates &middot;
        ${qc.gaps.length} gap(s) over 6h &middot; ${subordinateHilo.length} high/low events extracted
      </div>
      ${qc.recordDays < 29.5 ? `<div class="small" style="margin-top:4px; color:#fbbf24;">Record is shorter than one full lunar cycle (~29.5 days) -- expect larger error, and TBYT (not MMSC) will likely be used.</div>` : ""}
    ` : `<div class="small">No file loaded yet.</div>`;
    return `
      <div class="small" style="margin-top:10px;">CSV with two columns: timestamp, water level (header row optional, any consistent units/interval).</div>
      <input id="gw-file" type="file" accept=".csv,.txt" style="width:100%; margin-top:6px;" />
      ${qcSummary}
      ${subordinateHilo.length ? `
        <div class="small" style="margin-top:8px;">Preview of extracted events (first 5 / last 5):</div>
        <div style="overflow:auto; max-height:200px;">
          <table style="width:100%; border-collapse:collapse;">
            <thead><tr><th style="text-align:left;">Type</th><th style="text-align:left;">Time (UTC)</th><th style="text-align:right;">Raw value</th></tr></thead>
            <tbody>${[...subordinateHilo.slice(0, 5), ...subordinateHilo.slice(-5)].map((e) => `
              <tr><td>${esc(e.type === "H" ? "High" : "Low")}</td><td>${esc(fmtTime(e.t))}</td><td style="text-align:right;">${fmt(e.v, 3)}</td></tr>
            `).join("")}</tbody>
          </table>
        </div>
        <button id="gw-export-hilo" class="btn" style="margin-top:6px;">Download extracted hi/lo (CSV)</button>
      ` : ""}
      <div class="row lm-add-row" style="margin-top:10px;">
        <button id="gw-next-2" class="btn primary" ${qc?.clean?.length ? "" : "disabled"}>Next: Control station</button>
      </div>
    `;
  }

  function renderControlStep() {
    return `
      <div class="row lm-add-row" style="margin-top:10px;">
        <button id="gw-find-stations" class="btn" ${busy ? "disabled" : ""}>Find nearby CO-OPS stations</button>
      </div>
      <div class="row lm-add-row" style="margin-top:6px;">
        <select id="gw-control" style="flex:1;">
          ${candidates.map((c) => `<option value="${esc(c.station.id)}"${c.station.id === state.controlStationId ? " selected" : ""}>${esc(c.station.name)} (${esc(c.station.id)}, ${c.distanceKm.toFixed(0)} km)</option>`).join("") || `<option value="">-- search first --</option>`}
        </select>
      </div>
      <div class="lm-title" style="margin-top:10px;">Comparison method</div>
      <select id="gw-method" style="width:100%;">
        <option value="auto"${state.method === "auto" ? " selected" : ""}>AUTO (MMSC if &ge;25 days of overlap, else TBYT)</option>
        <option value="mmsc"${state.method === "mmsc" ? " selected" : ""}>MMSC -- Monthly Mean Simultaneous Comparison (&ge;~1 month)</option>
        <option value="tbyt"${state.method === "tbyt" ? " selected" : ""}>TBYT -- Tide-by-Tide Comparison (shorter records)</option>
        <option value="fred"${state.method === "fred" ? " selected" : ""}>FRED -- Direct average, no control station (reports Station Datum, not a national datum)</option>
      </select>
      <div class="row lm-add-row" style="margin-top:10px;">
        <button id="gw-run" class="btn primary" ${busy ? "disabled" : ""}>Compute preliminary datums</button>
      </div>
    `;
  }

  function renderResultsStep() {
    if (!result) return `<div class="small" style="margin-top:10px;">No results yet -- run step 3/4 first.</div>`;
    const rows = [["MHHW", result.stats.MHHW], ["MHW", result.stats.MHW], ["MTL", result.stats.MTL], ["DTL", result.stats.DTL], ["MLW", result.stats.MLW], ["MLLW", result.stats.MLLW]];
    const datumRows = rows.map(([name, v]) => `<tr><td>${name}</td><td style="text-align:right;">${fmt(v, 3)} m</td></tr>`).join("");
    return `
      <div class="small" style="background:rgba(251,191,36,0.1); border:1px solid rgba(251,191,36,0.35); padding:6px 8px; border-radius:6px; margin:10px 0;">
        Preliminary -- not a certified NOS tidal datum. Error estimate is a rough, illustrative heuristic, not NOAA's calibrated model.
      </div>
      <div class="small">
        Method <strong>${esc(result.method.toUpperCase())}</strong> &middot; frame ${esc(result.frame)}
        ${result.controlStationId ? ` &middot; control ${esc(result.controlStationName)} (${esc(result.controlStationId)})` : ""}
      </div>
      <table style="width:100%; border-collapse:collapse; margin-top:8px;">
        <thead><tr><th style="text-align:left;">Datum</th><th style="text-align:right;">Value</th></tr></thead>
        <tbody>${datumRows}</tbody>
      </table>
      <div class="small" style="margin-top:6px;">Rough error estimate: &plusmn; ${fmt(result.errorM, 3)} m</div>
      <div class="row lm-add-row" style="margin-top:10px;">
        <button id="gw-export-report" class="btn primary">Download report (HTML)</button>
      </div>
    `;
  }

  function render() {
    if (!containerEl) return;
    const body = {
      metadata: renderMetadataStep,
      upload: renderUploadStep,
      control: renderControlStep,
      results: renderResultsStep,
    }[state.step]?.() ?? "";

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Gauge Datum Wizard</div>
        <div class="small" style="margin-top:6px;">
          Import a field gauge record and work out a preliminary local tidal datum against a nearby NOAA station.
        </div>
        <div style="margin-top:10px;">${renderStepper()}</div>
        ${body}
        <div class="small" style="margin-top:10px; color:var(--muted);">${esc(status)}</div>
      </div>
    `;

    containerEl.querySelectorAll("[data-gw-step]").forEach((btn) => {
      btn.addEventListener("click", () => { readForm(); goStep(btn.dataset.gwStep); });
    });
    containerEl.querySelector("#gw-next-1")?.addEventListener("click", () => { readForm(); goStep("upload"); });
    containerEl.querySelector("#gw-next-2")?.addEventListener("click", () => { readForm(); goStep("control"); });
    containerEl.querySelector("#gw-file")?.addEventListener("change", (e) => onFile(e.target.files?.[0]));
    containerEl.querySelector("#gw-export-hilo")?.addEventListener("click", exportHiLoCsv);
    containerEl.querySelector("#gw-find-stations")?.addEventListener("click", findStations);
    containerEl.querySelector("#gw-run")?.addEventListener("click", runCompute);
    containerEl.querySelector("#gw-export-report")?.addEventListener("click", exportReport);
    ["#gw-sensor", "#gw-install", "#gw-removal", "#gw-lat", "#gw-lon", "#gw-units", "#gw-benchmarks", "#gw-method", "#gw-control"].forEach((sel) => {
      containerEl.querySelector(sel)?.addEventListener("change", readForm);
    });
  }

  function mount(el) {
    containerEl = el;
    render();
  }

  function destroy() {
    containerEl = null;
  }

  function snapshot() { return { state }; }
  function restore(data) {
    if (data?.state) state = { ...state, ...data.state };
    render();
  }

  return { mount, destroy, snapshot, restore };
}
